import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import "htmx.org";
import Alpine from "alpinejs";
import focus from "@alpinejs/focus";
import { initCocktailSlider } from "./cocktail-slider";
import GLightbox from "glightbox";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);
window.htmx = require("htmx.org");

window.Alpine = Alpine;

Alpine.plugin(focus);

// ---------------------------------------------------------------------------
// Alpine data factory: live price indication for the booking / inquiry forms.
// Must be defined BEFORE Alpine.start() so x-data="craftandmotionBookingCalc()"
// can resolve on first paint.
// ---------------------------------------------------------------------------
window.craftandmotionBookingCalc = function (seed) {
  const opts = seed || {};
  return {
    // ---- Form values (bound to the inputs via x-model) ----
    date: "",
    time: "",
    eventType: opts.eventType || "",
    duration: opts.duration || "",
    participants: 6,
    participantsUnknown: false,
    cocktailIntensity: "standard",
    expressActive: false,
    expressCocktails: 20,
    pricePanelInView: true,

    // ---- Pricing constants (edit here to update the calculator) ----
    BASE_FEE_CATERING: 250,
    BASE_FEE_WORKSHOP: 150,
    // Catering logistics surcharge above this headcount: €5 per extra person.
    CATERING_LOGISTICS_FREE_UP_TO: 20,
    CATERING_LOGISTICS_PER_EXTRA_PERSON: 5,
    LABOR_RATE_PER_HOUR: 60,
    CATERING_PRICE_PER_COCKTAIL: 6,
    // Cocktails per person per event (duration does not change the count).
    // Set true to show the 1.5 / 2 / 2.5 radio (forms, calculator, emails).
    // Also uncomment the "Type moment" rows in the booking email templates.
    SHOW_COCKTAIL_INTENSITY: false,
    COCKTAIL_INTENSITY_DEFAULT: "standard",
    COCKTAILS_PER_PERSON_BY_INTENSITY: {
      light: 1.5,
      standard: 2,
      full: 2.5,
    },
    WORKSHOP_PRICE_ADULT: 15,
    MAX_COCKTAILS_PER_HOUR: 40,
    WORKSHOP_DURATION: "2 uur",
    EXPRESS_PRICE_PER_COCKTAIL: 5,
    EXPRESS_PREP_FEE: 100,

    // ---- Label templates (populated from data-* attrs in init) ----
    _labels: {
      baseFee: "Logistiek, barhuur & premium glaswerk",
      labor: "Arbeid ({hours} uur × € {rate})",
      workshop: "{count} pers × € {pp} ({hours} uur)",
      catering:
        "Drankarrangement van {total} cocktails ({count} × {per_person}) × € {price}",
      perPerson: "Dit is {amount} per persoon",
      expressPrep: "Express Cocktail Service voorbereiding",
      expressCocktails: "{count} pre-batch cocktails × € {price}",
      cocktailsHint: "We rekenen standaard met 2 cocktails per persoon.",
      intensityLight: "Lichte borrel of zakelijke netwerkavond",
      intensityStandard: "Gemiddeld feest of bruiloft (Standaard)",
      intensityFull: "Avondvullend feest met echte liefhebbers",
    },
    _labelsExtra: {
      priceTotal: "Totaal (excl. BTW)",
      priceOnRequest: "Prijs op aanvraag",
    },

    init() {
      const raw = opts.participants;
      if (raw === "Onbekend") {
        this.participantsUnknown = true;
      } else if (raw && !isNaN(parseInt(raw, 10))) {
        this.participants = Math.max(8, parseInt(raw, 10));
      }
      // Pull translated line-label templates from data-* attributes on the
      // Alpine root element (rendered server-side from lexicon strings).
      const ds = this.$el ? this.$el.dataset : {};
      if (ds.labelBaseFee) this._labels.baseFee = ds.labelBaseFee;
      if (ds.labelLabor) this._labels.labor = ds.labelLabor;
      if (ds.labelWorkshop) this._labels.workshop = ds.labelWorkshop;
      if (ds.labelCatering) this._labels.catering = ds.labelCatering;
      if (ds.labelPerPerson) this._labels.perPerson = ds.labelPerPerson;
      if (ds.labelExpressPrep) this._labels.expressPrep = ds.labelExpressPrep;
      if (ds.labelExpressCocktails)
        this._labels.expressCocktails = ds.labelExpressCocktails;
      if (ds.labelCocktailsHint)
        this._labels.cocktailsHint = ds.labelCocktailsHint;
      if (ds.labelIntensityLight)
        this._labels.intensityLight = ds.labelIntensityLight;
      if (ds.labelIntensityStandard)
        this._labels.intensityStandard = ds.labelIntensityStandard;
      if (ds.labelIntensityFull)
        this._labels.intensityFull = ds.labelIntensityFull;
      if (ds.labelPriceTotal) this._labelsExtra.priceTotal = ds.labelPriceTotal;
      if (ds.labelPriceOnRequest)
        this._labelsExtra.priceOnRequest = ds.labelPriceOnRequest;
      this.$watch("eventType", () => {
        this.normalizeEventType();
        this.normalizeDurationForEventType();
      });
      this.normalizeEventType();
      this.normalizeDurationForEventType();
      this.normalizeCocktailIntensity();
      this.$nextTick(() => this.bindPricePanelObserver());
    },

    _getPricePanel() {
      return document.getElementById("booking-price-panel");
    },

    bindPricePanelObserver() {
      const panel = this._getPricePanel();
      if (!panel || typeof IntersectionObserver === "undefined") return;

      if (this._pricePanelObserver) {
        this._pricePanelObserver.disconnect();
      }

      this._pricePanelObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.pricePanelInView = entry.isIntersecting;
          });
        },
        { root: null, threshold: 0.12 },
      );
      this._pricePanelObserver.observe(panel);
    },

    isMobileViewport() {
      return (
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 1023px)").matches
      );
    },

    showMobilePricePreview() {
      return this.isMobileViewport() && !this.pricePanelInView;
    },

    mobilePricePreviewAmount() {
      if (!this.canCalculate()) return this._labelsExtra.priceOnRequest;
      return this.fmt(this.breakdown().subtotal);
    },

    scrollToPricePanel() {
      const panel = this._getPricePanel();
      if (!panel) return;
      panel.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    _escapeHtml(str) {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },

    normalizeEventType() {
      const valid = ["Catering", "Cocktail Workshop"];
      if (this.eventType && !valid.includes(this.eventType)) {
        this.eventType = "";
      }
    },

    syncFormStateFromDom(form) {
      if (!form) return;
      const wt = form.querySelector('[name="workshop_type"]');
      const dur = form.querySelector('[name="duration"]');
      const unknownCheck = form.querySelector("[data-participants-unknown]");
      const numInput = form.querySelector('input[type="number"][min="8"]');
      const intensityHidden = form.querySelector(
        'input[type="hidden"][name="cocktail_intensity"]',
      );
      const intensityRadio = form.querySelector(
        "[data-cocktail-intensity]:checked",
      );
      const exprFlag = form.querySelector('[name="express_active"]');
      const exprCount = form.querySelector('[name="express_cocktails"]');
      if (wt) this.eventType = wt.value;
      this.normalizeEventType();
      if (wt && wt.value !== this.eventType) wt.value = this.eventType;
      if (dur) this.duration = dur.value;
      if (unknownCheck?.checked) {
        this.participantsUnknown = true;
      } else {
        this.participantsUnknown = false;
        const n = parseInt(numInput?.value, 10);
        this.participants = isNaN(n) ? 8 : n;
      }
      if (intensityHidden?.value) {
        this.cocktailIntensity = intensityHidden.value;
      } else if (intensityRadio) {
        this.cocktailIntensity = intensityRadio.value;
      }
      this.normalizeCocktailIntensity();
      if (exprFlag) this.expressActive = exprFlag.value === "Ja";
      if (exprCount) {
        const n = parseInt(exprCount.value, 10);
        this.expressCocktails = isNaN(n) ? 0 : Math.max(0, n);
      }
      this.normalizeDurationForEventType();
    },

    buildPriceIndicationHtml() {
      if (!this.canCalculate()) {
        return `<p style="margin:0;color:#64748b">${this._escapeHtml(this._labelsExtra.priceOnRequest)}</p>`;
      }
      const b = this.breakdown();
      const rows = b.lines
        .map(
          (l) =>
            `<tr><td style="padding:4px 8px;background:#f1f5f9">${this._escapeHtml(l.label)}</td><td style="padding:4px 8px;text-align:right;white-space:nowrap">${this._escapeHtml(this.fmt(l.amount))}</td></tr>`,
        )
        .join("");
      const totalRow = `<tr><td style="padding:6px 8px;font-weight:bold;border-top:1px solid #e2e8f0">${this._escapeHtml(this._labelsExtra.priceTotal)}</td><td style="padding:6px 8px;font-weight:bold;text-align:right;border-top:1px solid #e2e8f0">${this._escapeHtml(this.fmt(b.subtotal))}</td></tr>`;
      const perPerson = this.pricePerPersonLabel();
      const perPersonHtml = perPerson
        ? `<p style="margin:8px 0 0;font-weight:500">${this._escapeHtml(perPerson)}</p>`
        : "";
      return `<table cellpadding="0" cellspacing="0" style="border-collapse:collapse;width:100%">${rows}${totalRow}</table>${perPersonHtml}`;
    },

    syncPriceIndicationForSubmit(form) {
      this.syncFormStateFromDom(form);
      const hidden = form?.querySelector('[name="price_indication_html"]');
      if (hidden) hidden.value = this.buildPriceIndicationHtml();
    },

    _interpolate(tpl, vars) {
      return tpl.replace(/\{(\w+)\}/g, (_, k) =>
        Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : "",
      );
    },

    // ---- Type helpers ----
    isWorkshop() {
      return this.eventType === "Cocktail Workshop";
    },
    isCatering() {
      return this.eventType === "Catering";
    },
    // ---- Express Cocktail Service (pre-batched cocktails add-on) ----
    showExpressOption() {
      return this.isCatering();
    },
    showCocktailIntensityOption() {
      return this.SHOW_COCKTAIL_INTENSITY && this.isCatering();
    },
    normalizeCocktailIntensity() {
      if (!this.SHOW_COCKTAIL_INTENSITY) {
        this.cocktailIntensity = this.COCKTAIL_INTENSITY_DEFAULT;
        return;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.COCKTAILS_PER_PERSON_BY_INTENSITY,
          this.cocktailIntensity,
        )
      ) {
        this.cocktailIntensity = this.COCKTAIL_INTENSITY_DEFAULT;
      }
    },
    cocktailIntensityLabel() {
      if (this.cocktailIntensity === "light")
        return this._labels.intensityLight;
      if (this.cocktailIntensity === "full") return this._labels.intensityFull;
      return this._labels.intensityStandard;
    },
    cocktailsCalcHint() {
      return this._interpolate(this._labels.cocktailsHint, {
        per_person: this.fmtCocktailsPp(this.cocktailsPerPerson()),
      });
    },
    fmtCocktailsPp(n) {
      return Number(n).toLocaleString("nl-NL", {
        minimumFractionDigits: Number(n) % 1 === 0 ? 0 : 1,
        maximumFractionDigits: 1,
      });
    },
    expressIsActive() {
      return this.expressActive && this.showExpressOption();
    },
    expressCocktailCount() {
      return Math.max(0, parseInt(this.expressCocktails, 10) || 0);
    },
    expressAmount() {
      if (!this.expressIsActive()) return 0;
      const n = this.expressCocktailCount();
      if (n === 0) return 0;
      return this.EXPRESS_PREP_FEE + n * this.EXPRESS_PRICE_PER_COCKTAIL;
    },
    showLargeGroupNotice() {
      if (this.participantsUnknown) return false;
      const p = parseInt(this.participants, 10);
      if (isNaN(p) || p <= 60) return false;
      return this.isWorkshop() || this.isCatering();
    },

    showWorkshopLargeGroupNotice() {
      if (this.participantsUnknown || !this.isWorkshop()) return false;
      const p = parseInt(this.participants, 10);
      return !isNaN(p) && p > 20;
    },

    workshopCocktailsPerPerson() {
      return this.cocktailsPerPerson();
    },

    cocktailsPerPerson() {
      const rates = this.COCKTAILS_PER_PERSON_BY_INTENSITY;
      if (!this.SHOW_COCKTAIL_INTENSITY) {
        return rates[this.COCKTAIL_INTENSITY_DEFAULT];
      }
      if (Object.prototype.hasOwnProperty.call(rates, this.cocktailIntensity)) {
        return rates[this.cocktailIntensity];
      }
      return rates[this.COCKTAIL_INTENSITY_DEFAULT];
    },

    totalCocktails() {
      if (this.participantsUnknown) return null;
      if (!this.isCatering() && !this.isWorkshop()) return null;
      const p = parseInt(this.participants, 10);
      const pp = this.cocktailsPerPerson();
      if (isNaN(p) || p < 1 || pp === null) return null;
      return Math.round(p * pp);
    },

    cocktailsPerHour() {
      const h = this.durationHours();
      const total = this.totalCocktails();
      if (h === null || h < 1 || total === null) return null;
      return total / h;
    },

    showHighCocktailRateNotice() {
      const rate = this.cocktailsPerHour();
      return rate !== null && rate > this.MAX_COCKTAILS_PER_HOUR;
    },
    normalizeDurationForEventType() {
      if (!this.isWorkshop()) return;
      this.duration = this.WORKSHOP_DURATION;
    },
    durationHours() {
      if (this.duration === "2 uur") return 2;
      if (this.duration === "3 uur") return 3;
      if (this.duration === "4 uur") return 4;
      return null;
    },
    isCustomDuration() {
      return (
        this.duration === "Onbekend / Maatwerk" ||
        this.duration === "Meer dan 4 uur"
      );
    },

    workshopPricePerPerson() {
      return this.WORKSHOP_PRICE_ADULT;
    },

    baseFeeAmount() {
      if (this.isWorkshop()) return this.BASE_FEE_WORKSHOP;

      const p = Math.max(8, parseInt(this.participants, 10) || 8);
      const extra = Math.max(0, p - this.CATERING_LOGISTICS_FREE_UP_TO);
      return (
        this.BASE_FEE_CATERING +
        extra * this.CATERING_LOGISTICS_PER_EXTRA_PERSON
      );
    },

    cateringCocktailsPerPerson() {
      return this.cocktailsPerPerson();
    },

    laborAmount() {
      const h = this.durationHours();
      if (h === null) return 0;
      return h * this.LABOR_RATE_PER_HOUR;
    },

    addLaborLine(lines) {
      const h = this.durationHours();
      lines.push({
        label: this._interpolate(this._labels.labor, {
          hours: h,
          rate: this.LABOR_RATE_PER_HOUR,
        }),
        amount: this.laborAmount(),
      });
    },

    canCalculate() {
      if (!this.eventType) return false;
      if (this.participantsUnknown) return false;
      if (!this.duration || this.isCustomDuration()) return false;
      if (this.durationHours() === null) return false;
      return this.isWorkshop() || this.isCatering();
    },

    breakdown() {
      if (!this.canCalculate()) return null;
      const p = Math.max(8, parseInt(this.participants, 10) || 8);
      const lines = [
        { label: this._labels.baseFee, amount: this.baseFeeAmount() },
      ];
      this.addLaborLine(lines);

      if (this.isWorkshop()) {
        const pp = this.workshopPricePerPerson();
        lines.push({
          label: this._interpolate(this._labels.workshop, {
            count: p,
            pp: pp,
            hours: this.durationHours(),
          }),
          amount: p * pp,
        });
      } else if (this.isCatering()) {
        const cocktailsPP = this.cateringCocktailsPerPerson();
        const total = Math.round(p * cocktailsPP);
        lines.push({
          label: this._interpolate(this._labels.catering, {
            count: p,
            per_person: this.fmtCocktailsPp(cocktailsPP),
            total: total,
            price: this.CATERING_PRICE_PER_COCKTAIL,
          }),
          amount: total * this.CATERING_PRICE_PER_COCKTAIL,
        });
      }

      if (this.expressIsActive()) {
        const n = this.expressCocktailCount();
        if (n > 0) {
          lines.push({
            label: this._labels.expressPrep,
            amount: this.EXPRESS_PREP_FEE,
          });
          lines.push({
            label: this._interpolate(this._labels.expressCocktails, {
              count: n,
              price: this.EXPRESS_PRICE_PER_COCKTAIL,
            }),
            amount: n * this.EXPRESS_PRICE_PER_COCKTAIL,
          });
        }
      }

      const subtotal = lines.reduce((s, l) => s + l.amount, 0);
      return { lines, subtotal };
    },

    pricePerPersonLabel() {
      if (!this.canCalculate()) return "";
      const p = Math.max(8, parseInt(this.participants, 10) || 8);
      const perHead = this.breakdown().subtotal / p;
      return this._interpolate(this._labels.perPerson, {
        amount: this.fmt(perHead),
      });
    },

    // ---- Formatting ----
    fmt(n) {
      return "€ " + Math.round(n).toLocaleString("nl-NL");
    },
  };
};

Alpine.start();

/** Booking form with price breakdown (HTMX submits before Alpine @submit). */
function getBookingCalcData(form) {
  const root = form?.closest("[data-booking-calc]");
  if (!root || !window.Alpine) return null;
  return window.Alpine.$data(root);
}

function syncBookingPriceIndication(form) {
  if (!form?.querySelector('[name="price_indication_html"]')) return;
  const calc = getBookingCalcData(form);
  if (calc?.syncPriceIndicationForSubmit) {
    calc.syncPriceIndicationForSubmit(form);
  }
}

// Capture phase: run before HTMX reads FormData.
document.addEventListener(
  "submit",
  (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    syncBookingPriceIndication(event.target);
  },
  true,
);

// Fallback: patch HTMX parameters if they were built already.
document.body.addEventListener("htmx:configRequest", (event) => {
  const elt = event.detail?.elt;
  const form = elt instanceof HTMLFormElement ? elt : elt?.closest?.("form");
  if (!form?.querySelector('[name="price_indication_html"]')) return;
  syncBookingPriceIndication(form);
  const hidden = form.querySelector('[name="price_indication_html"]');
  if (!hidden) return;
  const params = event.detail.parameters;
  if (params instanceof FormData) {
    params.set("price_indication_html", hidden.value);
  } else if (params && typeof params === "object") {
    params.price_indication_html = hidden.value;
  }
});

const scrollFormIntoView = (element) => {
  if (!element) return;
  if (typeof gsap === "undefined") {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  gsap.to(window, {
    scrollTo: {
      y: element,
      offsetY: 180,
    },
    duration: 1,
    ease: "power2.out",
  });
};

document.body.addEventListener("htmx:afterSwap", (event) => {
  const target = event.detail?.target;
  if (!target || !window.Alpine) return;

  const form =
    target.querySelector?.("form") ??
    (target.matches?.("form") ? target : null);
  const calc = form ? getBookingCalcData(form) : null;
  if (calc?.syncFormStateFromDom) {
    calc.syncFormStateFromDom(form);
  }

  window.Alpine.initTree(target);

  if (calc?.syncFormStateFromDom) {
    calc.syncFormStateFromDom(form);
  }

  if (target.id === "availability-result") {
    requestAnimationFrame(() => scrollFormIntoView(target));
  }
});

function samePageScroll() {
  // Detect if a link's href goes to the current page
  function getSamePageAnchor(link) {
    if (
      link.protocol !== window.location.protocol ||
      link.host !== window.location.host ||
      link.pathname !== window.location.pathname ||
      link.search !== window.location.search
    ) {
      return false;
    }
    return link.hash;
  }
  // Scroll to a given hash, preventing the event given if there is one
  function scrollToHash(hash, e) {
    const elem = hash ? document.querySelector(hash) : false;
    if (elem) {
      if (e) e.preventDefault();
      gsap.to(window, { scrollTo: { y: elem, offsetY: 237 } });
    }
  }
  // If a link's href is within the current page, scroll to it instead
  document.querySelectorAll("a[href]").forEach((a) => {
    a.addEventListener("click", (e) => {
      scrollToHash(getSamePageAnchor(a), e);
    });
  });
  // Scroll to the element in the URL's hash on load
  scrollToHash(window.location.hash);
}

// Constants
const SCROLL_SETTINGS = {
  NAV_HEIGHT: 120,
  DELTA: 60,
  INTERVAL: 250,
};

const hideNavOnScroll = () => {
  const nav = document.querySelector("#main_nav");
  if (!nav) return;

  let lastScrollY = SCROLL_SETTINGS.NAV_HEIGHT;
  let didScroll = false;

  const scrolled = () => {
    const currentScroll = window.scrollY;
    if (Math.abs(lastScrollY - currentScroll) > SCROLL_SETTINGS.DELTA) {
      if (
        currentScroll > lastScrollY &&
        currentScroll > SCROLL_SETTINGS.NAV_HEIGHT
      ) {
        nav.classList.add("nav--hidden");
      } else if (currentScroll < lastScrollY) {
        nav.classList.remove("nav--hidden");
      }
      lastScrollY = currentScroll;
    }
  };

  window.addEventListener("scroll", () => {
    didScroll = true;
  });

  setInterval(() => {
    if (didScroll) {
      scrolled();
      didScroll = false;
    }
  }, SCROLL_SETTINGS.INTERVAL);
};

const isTopLevelAnimated = (el) => {
  const parentAnimated = el.parentElement?.closest("[data-animate-on-scroll]");
  return !parentAnimated || parentAnimated === el;
};

const markScrollSectionsInView = (elements) => {
  elements.forEach((el) => el.classList.add("is-in-view"));
};

const initScrollAnimations = () => {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    return;
  }

  const animated = document.querySelectorAll(
    "[data-animate-on-scroll]:not(#main_nav)",
  );

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    markScrollSectionsInView(animated);
    return;
  }

  const sections = Array.from(animated).filter(isTopLevelAnimated);
  const visibleSections = [];
  const hiddenSections = [];

  sections.forEach((section) => {
    const alreadyVisible =
      section.getBoundingClientRect().top < window.innerHeight * 0.85;

    if (alreadyVisible) {
      visibleSections.push(section);
    } else {
      hiddenSections.push(section);
    }
  });

  if (visibleSections.length > 0) {
    gsap.to(visibleSections, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: "power2.out",
      stagger: {
        amount: 0.7,
        from: "start",
      },
      onComplete() {
        markScrollSectionsInView(this.targets());
      },
    });
  }

  hiddenSections.forEach((section) => {
    gsap.to(section, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: "power2.out",
      scrollTrigger: {
        trigger: section,
        start: "top 85%",
        toggleActions: "play none none reverse",
        onEnter: () => section.classList.add("is-in-view"),
        onLeaveBack: () => section.classList.remove("is-in-view"),
      },
      onComplete: () => section.classList.add("is-in-view"),
    });
  });
};

(function calComInit(C, A, L) {
  let p = function (a, ar) {
    a.q.push(ar);
  };
  let d = C.document;
  C.Cal =
    C.Cal ||
    function () {
      let cal = C.Cal;
      let ar = arguments;
      if (!cal.loaded) {
        cal.ns = {};
        cal.q = cal.q || [];
        d.head.appendChild(d.createElement("script")).src = A;
        cal.loaded = true;
      }
      if (ar[0] === L) {
        const api = function () {
          p(api, arguments);
        };
        const namespace = ar[1];
        api.q = api.q || [];
        if (typeof namespace === "string") {
          cal.ns[namespace] = cal.ns[namespace] || api;
          p(cal.ns[namespace], ar);
          p(cal, ["initNamespace", namespace]);
        } else p(cal, ar);
        return;
      }
      p(cal, ar);
    };
})(window, "https://app.cal.com/embed/embed.js", "init");
Cal("init", "30min", { origin: "https://app.cal.com" });

// Important: Please add the following attributes to the element that should trigger the calendar to open upon clicking.
// `data-cal-link="menno-pietersen-any-screen-size-42cra9/30min"`
// data-cal-namespace="30min"
// `data-cal-config='{"layout":"month_view","theme":"light"}'`

Cal.ns["30min"]("ui", {
  theme: "light",
  hideEventTypeDetails: false,
  layout: "month_view",
});

// Resume muted autoplay videos after bfcache back-navigation (optional fallback).
function resumeAutoplayVideos() {
  document.querySelectorAll("video[autoplay][muted]").forEach((video) => {
    if (video.paused) video.play().catch(() => {});
  });
}

window.addEventListener("pageshow", resumeAutoplayVideos);

function initLightbox() {
  var lightbox = GLightbox();
  var lightboxDescription = GLightbox({
    selector: ".glightbox2",
  });
  var lightboxVideo = GLightbox({
    selector: ".glightbox3",
  });
  lightboxVideo.on("slide_changed", ({ prev, current }) => {
    console.log("Prev slide", prev);
    console.log("Current slide", current);

    const { slideIndex, slideNode, slideConfig, player } = current;

    if (player) {
      if (!player.ready) {
        // If player is not ready
        player.on("ready", (event) => {
          // Do something when video is ready
        });
      }

      player.on("play", (event) => {
        console.log("Started play");
      });

      player.on("volumechange", (event) => {
        console.log("Volume change");
      });

      player.on("ended", (event) => {
        console.log("Video ended");
      });
    }
  });
}

function init() {
  hideNavOnScroll();
  samePageScroll();
  initCocktailSlider();
  initScrollAnimations();
  initLightbox();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
